import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  RefreshCw, Search, Trash2, Edit2, ArrowRightLeft, MoreHorizontal, Clock, Users, Building2, Check
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";

const STATUS_COLORS = {
  waitlisted: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  converted: "bg-blue-100 text-blue-800",
  rejected: "bg-red-100 text-red-800",
};

const ALL_CATEGORIES = ['hair', 'makeup', 'nail', 'lash', 'event', 'instructor', 'culinary', 'wellness', 'travel', 'dispensary', 'style'];

export default function WaitlistManager() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editEntry, setEditEntry] = useState(null);
  const [editNotes, setEditNotes] = useState("");
  const [editStatus, setEditStatus] = useState("waitlisted");
  const [convertEntry, setConvertEntry] = useState(null);
  const [convertOrgName, setConvertOrgName] = useState("");
  const [saving, setSaving] = useState(false);

  // Iter 9.49 - Approve & Grant dialog state
  const [approveEntry, setApproveEntry] = useState(null);
  const [approveBypassIntake, setApproveBypassIntake] = useState(false);
  const [approveGrantPilotId, setApproveGrantPilotId] = useState("");
  const [approveGrantHostId, setApproveGrantHostId] = useState("");
  const [availablePilots, setAvailablePilots] = useState([]);

  useEffect(() => {
    // Load active pilot programs once for the approve dialog dropdown
    fetch(`${API}/admin/pilot-programs`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setAvailablePilots(d?.pilots || d?.programs || []))
      .catch(() => {});
  }, []);

  const openApprove = (entry) => {
    setApproveEntry(entry);
    setApproveBypassIntake(false);
    setApproveGrantPilotId("");
    setApproveGrantHostId("");
  };
  const handleApprove = async () => {
    if (!approveEntry) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/waitlist/${approveEntry.waitlist_id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          bypass_intake: approveBypassIntake,
          grant_pilot_id: approveGrantPilotId || null,
          grant_host_id: approveGrantHostId || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Approved. Magic link: ${data.magic_link_token}`);
        setApproveEntry(null);
        fetchEntries();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Approve failed");
      }
    } catch (e) {
      toast.error("Approve failed");
    }
    setSaving(false);
  };

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("session_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/waitlist/entries?limit=200`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (categoryFilter !== "all") url += `&category=${categoryFilter}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
        setTotal(data.total || 0);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [statusFilter, categoryFilter, getAuthHeaders]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const filteredEntries = entries.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (e.email || "").toLowerCase().includes(s) ||
      (e.name || "").toLowerCase().includes(s) ||
      (e.company || "").toLowerCase().includes(s);
  });

  const handleUpdate = async () => {
    if (!editEntry) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/waitlist/entries/${editEntry.waitlist_id}`, {
        method: "PUT",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ status: editStatus, admin_notes: editNotes }),
      });
      if (res.ok) {
        toast.success("Entry updated");
        setEditEntry(null);
        fetchEntries();
      } else { toast.error("Failed to update"); }
    } catch { toast.error("Error"); }
    setSaving(false);
  };

  const handleDelete = async (wlId) => {
    if (!window.confirm("Remove this waitlist entry?")) return;
    try {
      const res = await fetch(`${API}/waitlist/entries/${wlId}`, {
        method: "DELETE", headers: getAuthHeaders(),
      });
      if (res.ok) { toast.success("Deleted"); fetchEntries(); }
      else { toast.error("Failed to delete"); }
    } catch { toast.error("Error"); }
  };

  const handleConvert = async () => {
    if (!convertEntry) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/waitlist/entries/${convertEntry.waitlist_id}/convert-to-org`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ org_name: convertOrgName || convertEntry.name, category: convertEntry.category }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Converted to org: ${data.org_name}`);
        setConvertEntry(null);
        fetchEntries();
      } else { toast.error("Failed to convert"); }
    } catch { toast.error("Error"); }
    setSaving(false);
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setEditStatus(entry.status || "waitlisted");
    setEditNotes(entry.admin_notes || "");
  };

  const openConvert = (entry) => {
    setConvertEntry(entry);
    setConvertOrgName(entry.company || entry.name || "");
  };

  return (
    <Card className="shadow-lg border-gray-200" data-testid="waitlist-manager">
      <CardContent className="p-4 md:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-64"
                data-testid="waitlist-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36" data-testid="waitlist-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="waitlisted">Waitlisted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-36" data-testid="waitlist-category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {ALL_CATEGORIES.map(c => (
                  <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{filteredEntries.length} of {total}</span>
            <Button variant="outline" size="sm" onClick={fetchEntries} data-testid="waitlist-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-12 text-gray-400" data-testid="waitlist-empty">
            <Clock className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No waitlist entries</p>
            <p className="text-sm mt-1">Entries will appear when users join the waitlist</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Advocate</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.map((entry) => (
                  <TableRow key={entry.waitlist_id} data-testid={`wl-row-${entry.waitlist_id}`}>
                    <TableCell className="font-medium text-sm">{entry.email}</TableCell>
                    <TableCell className="text-sm">{entry.name || "-"}</TableCell>
                    <TableCell className="text-sm">{entry.company || "-"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize text-xs">{entry.category || "-"}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${STATUS_COLORS[entry.status] || "bg-gray-100 text-gray-800"}`}>
                        {entry.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {entry.advocate_info ? entry.advocate_info.name || entry.advocate_info.email : entry.advocate_code || "-"}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : "-"}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500 max-w-[120px] truncate">
                      {entry.admin_notes || "-"}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`wl-actions-${entry.waitlist_id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(entry)}>
                            <Edit2 className="w-3 h-3 mr-2" /> Edit
                          </DropdownMenuItem>
                          {entry.status !== "approved" && entry.status !== "converted" && (
                            <DropdownMenuItem onClick={() => openApprove(entry)} data-testid={`wl-approve-${entry.waitlist_id}`}>
                              <Check className="w-3 h-3 mr-2" /> Approve & Grant
                            </DropdownMenuItem>
                          )}
                          {entry.status !== "converted" && (
                            <DropdownMenuItem onClick={() => openConvert(entry)}>
                              <ArrowRightLeft className="w-3 h-3 mr-2" /> Convert to Org
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(entry.waitlist_id)}>
                            <Trash2 className="w-3 h-3 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={!!editEntry} onOpenChange={(open) => !open && setEditEntry(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Waitlist Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm font-medium mb-1">Email</p>
              <p className="text-sm text-gray-600">{editEntry?.email}</p>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Status</p>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger data-testid="edit-wl-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="waitlisted">Waitlisted</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Admin Notes</p>
              <Textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Internal notes about this entry..."
                data-testid="edit-wl-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEntry(null)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={saving} data-testid="save-wl-edit">
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Org Dialog */}
      <Dialog open={!!convertEntry} onOpenChange={(open) => !open && setConvertEntry(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to Organization</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              Convert <strong>{convertEntry?.email}</strong> from the waitlist into an organization.
            </p>
            <div>
              <p className="text-sm font-medium mb-1">Host Name</p>
              <Input
                value={convertOrgName}
                onChange={(e) => setConvertOrgName(e.target.value)}
                placeholder="Org name..."
                data-testid="convert-org-name"
              />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Category</p>
              <Badge variant="outline" className="capitalize">{convertEntry?.category || "event"}</Badge>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertEntry(null)}>Cancel</Button>
            <Button onClick={handleConvert} disabled={saving} data-testid="confirm-convert-org">
              {saving ? "Converting..." : "Convert to Org"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve & Grant Dialog (Iter 9.49) */}
      <Dialog open={!!approveEntry} onOpenChange={(open) => !open && setApproveEntry(null)}>
        <DialogContent data-testid="approve-grant-dialog">
          <DialogHeader>
            <DialogTitle>Approve & grant access</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm text-gray-600">Approving</p>
              <p className="text-sm font-semibold">{approveEntry?.email}</p>
              <Badge variant="outline" className="mt-1 capitalize">{approveEntry?.category}</Badge>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Grant pilot membership (optional)</p>
              <Select value={approveGrantPilotId || "none"} onValueChange={(v) => setApproveGrantPilotId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="approve-pilot-select"><SelectValue placeholder="No pilot" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No pilot</SelectItem>
                  {availablePilots.map((p) => (
                    <SelectItem key={p.pilot_id || p.id} value={p.pilot_id || p.id}>
                      {p.name || p.label || p.pilot_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Adds the user to the pilot's enrolled list.</p>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Grant host dashboard ID (optional)</p>
              <Input
                value={approveGrantHostId}
                onChange={(e) => setApproveGrantHostId(e.target.value)}
                placeholder="host_id (leave blank to skip)"
                data-testid="approve-host-id-input"
              />
              <p className="text-xs text-gray-500 mt-1">Links this user as an owner of the given host dashboard.</p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={approveBypassIntake}
                onChange={(e) => setApproveBypassIntake(e.target.checked)}
                data-testid="approve-bypass-intake"
              />
              <span>Bypass intake (send straight to /me)</span>
            </label>
            <p className="text-xs text-gray-500">
              A Conversion row is always written. A magic link is generated.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveEntry(null)}>Cancel</Button>
            <Button onClick={handleApprove} disabled={saving} data-testid="confirm-approve-grant">
              {saving ? "Approving..." : "Approve & Grant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
